import { Box, Heading, Icon, Image, Modal, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconLockOpenFilled } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import propertyUtils from '../../../utils/propertyUtils';

import ChecklistDiamond from '../../../assets/rentalProtection/ChecklistDiamond.png';

import ExpandableDisclaimer from '../../RentalProtection/shared/ExpandableDisclaimer';
import ZillowInsuranceServicesLogo from '../../RentalProtection/shared/ZillowInsuranceServicesLogo';
import ModalFooter from '../ModalFooter';

import type { RentGuaranteeDisclaimerContentDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listingAlias: string;
  legalDisclaimer?: RentGuaranteeDisclaimerContentDto;
  coverageSummaryUrl?: string;
  coverageAmount?: string;
  onClose: () => void;
  onSubmit: () => void;
};

const RentGuaranteeActiveModal = ({
  listingAlias,
  legalDisclaimer,
  coverageSummaryUrl,
  coverageAmount,
  onClose,
  onSubmit,
}: Props) => {
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { formattedCompleteAddress } = propertyUtils.formatAddress(listingDetails || {});

  // storeEffectivePolicyScreenDisplayed is intentionally not called here — no generated
  // ts-client hook exists yet. The onSubmit/onClose handlers manage local dismissal only.

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<ZillowInsuranceServicesLogo />}
      body={
        <Box css={{ padding: { base: '200', md: '1200' } }}>
          <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'column', marginBottom: '200' }}>
            <Icon
              size="lg"
              css={{ color: 'icon.accent.blue.hero', marginRight: '400' }}
              render={<IconLockOpenFilled />}
            />
            <Heading level={2} fontFamily="heading-alt">
              Your Rental Protection coverage is effective
            </Heading>
          </Box>
          <Box css={{ marginInline: '200' }}>
            <Paragraph css={{ marginBottom: '400' }}>
              {`LeaseLock has confirmed that Rental Protection is in effect for ${formattedCompleteAddress}.`}
            </Paragraph>
            <Box
              css={{
                display: 'flex',
                padding: '400',
                marginBottom: '400',
                backgroundColor: 'bg.accent.blue.soft',
                width: '100%',
              }}
            >
              <Box>
                <Image src={ChecklistDiamond} alt="checklist" />
              </Box>
              <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                <Paragraph textStyle="body-bold">What&apos;s covered?</Paragraph>
                <Paragraph css={{ marginBottom: '400' }}>
                  Rental Protection provides up to {coverageAmount} of insurance coverage to help with some of the
                  situations your other insurance policies don&apos;t cover, like when tenants don&apos;t pay their
                  rent, or their puppy chews your unit&apos;s new carpet.
                </Paragraph>
                {coverageSummaryUrl && (
                  <TextButton asChild>
                    <a target="_blank" rel="noopener noreferrer" href={coverageSummaryUrl}>
                      See the coverage summary
                    </a>
                  </TextButton>
                )}
              </Box>
            </Box>
            {legalDisclaimer && (
              <ExpandableDisclaimer title="Coverage disclaimer" disclaimerContent={legalDisclaimer} />
            )}
          </Box>
        </Box>
      }
      footer={<ModalFooter submitText="OK" onSubmit={onSubmit} />}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default RentGuaranteeActiveModal;
